import { useEffect } from 'react';

interface LightboxProps {
  work: {
    id: number;
    image: string;
    title: string;
    category: string;
    description: string;
    year: string;
    technique: string;
  };
  onClose: () => void;
}

export default function GalleryLightbox({ work, onClose }: LightboxProps) {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-md p-8"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-6 right-6 w-12 h-12 flex items-center justify-center bg-neutral-800/80 hover:bg-neutral-700 text-white rounded-full transition-colors cursor-pointer z-10"
      >
        <i className="ri-close-line text-2xl"></i>
      </button>

      <div
        className="relative max-w-5xl w-full flex flex-col lg:flex-row bg-neutral-900 rounded-lg overflow-hidden border border-neutral-800 shadow-2xl max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Imagen */}
        <div className="relative w-full lg:w-2/3 h-[250px] sm:h-[350px] lg:h-[560px]">
          <img
            src={work.image}
            alt={work.title}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Info */}
        <div className="w-full lg:w-1/3 p-5 md:p-8 flex flex-col justify-between overflow-y-auto">
          <div>
            <span className="inline-block px-3 py-1 bg-amber-600/20 text-amber-500 text-xs font-bold uppercase tracking-wider mb-4">
              {work.category}
            </span>
            <h2
              className="text-3xl font-bold text-white mb-4"
              style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '2px' }}
            >
              {work.title}
            </h2>
            <p className="text-neutral-400 text-sm leading-relaxed mb-8">
              {work.description}
            </p>

            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-neutral-800 rounded">
                  <i className="ri-calendar-line text-amber-500 text-sm"></i>
                </div>
                <div>
                  <p className="text-neutral-600 text-xs uppercase tracking-wider">Año</p>
                  <p className="text-white text-sm font-medium">{work.year}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-neutral-800 rounded">
                  <i className="ri-brush-line text-amber-500 text-sm"></i>
                </div>
                <div>
                  <p className="text-neutral-600 text-xs uppercase tracking-wider">Técnica</p>
                  <p className="text-white text-sm font-medium">{work.technique}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-neutral-800 rounded">
                  <i className="ri-user-line text-amber-500 text-sm"></i>
                </div>
                <div>
                  <p className="text-neutral-600 text-xs uppercase tracking-wider">Artista</p>
                  <p className="text-white text-sm font-medium">Daro</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-neutral-800">
            <a
              href="https://www.instagram.com/Daro_tattoo_"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full py-3 bg-neutral-800 hover:bg-neutral-700 text-white text-sm font-medium transition-colors cursor-pointer rounded"
            >
              <i className="ri-instagram-line"></i>
              Ver más en Instagram
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
